import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router, ActivatedRoute} from '@angular/router';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import {SessionService} from '../../services/session.service';
import SessionModel from '../../services/session.model';
import UserModel from '../../services/user.model';
import PersonsModel from '../../services/persons.model';
import {PersonsService} from '../../services/persons.service';
import {BusinessEntityService} from '../../services/businessEntity.service';
import BusinessEntity from '../../services/businessEntity.model';
import {FarmSiteService} from '../../services/farmSite.service';
import FarmSiteModel from '../../services/farmSite.model';
import {FirebaseNotificationsMessagingService} from 'src/app/services/FirebaseMessaging.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import { PolicyCheckerService } from 'src/app/ACL/policyChecker.service';
import { AccessControllerService } from 'src/app/ACL/roleAccessController.service';

/**
 * LoginComponent
 * 
 * Core authentication and session management component for the Aqua Cloud application.
 * Handles user login, session creation, token validation, and role-based routing.
 * 
 * Key responsibilities:
 * - User authentication with username/password validation
 * - Session initialization and management
 * - Token-based login from external applications (Ionic)
 * - Role and permission-based navigation routing
 * - Business entity and person type resolution
 * - localStorage management for user session data
 * - Firebase messaging permission requests
 * - Access control and policy initialization
 * 
 * Supported authentication flows:
 * 1. Direct login - Username/password form submission
 * 2. Token-based login - From Ionic mobile app with token validation
 * 3. User ID-based login - Direct user ID parameter in URL
 * 4. Guest login - For unauthenticated users
 * 
 * Navigation routing logic based on:
 * - User person type (Farmer, Farm Manager, Provider, Business Admin, SME, Buyer, etc.)
 * - Business category (Farm, Provider, Others)
 * - Business account status (active/inactivated)
 * - Available user role permissions
 * - Access control policies
 * 
 * localStorage keys managed:
 * - isSessionExist: Session active flag
 * - aquLoginData: User information JSON
 * - businessID, personID, userID: Entity identifiers
 * - userName, userType: User credentials
 * - loginPersonType, loginPersonId, loginPersonName: Person details
 * - loginBusinessType, loginBusinessName, loginBusinessID: Business details
 * - authToken: Authentication token from external login
 * - redirectAfterLogin: Post-login navigation target
 * 
 * @component
 * @selector app-login
 * @example
 * <app-login></app-login>
 * 
 * @implements {OnInit}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})

export class LoginComponent implements OnInit {
  /** 
   * Array of business entities available for selection
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;
  
  /** 
   * Currently selected business entity with type, category, and status information
   * @type {BusinessEntity}
   */
  public businessEntity: BusinessEntity;
  
  /** 
   * Array of farm sites associated with the business
   * @type {Array<FarmSiteModel>}
   */
  public farmSites: Array<FarmSiteModel>;
  
  /** 
   * Currently selected farm site
   * @type {FarmSiteModel}
   */
  public farmSite: FarmSiteModel;
  
  /** 
   * Persons/staff members associated with the login business
   * @type {Array<PersonsModel>}
   */
  public loginBusinessPersons: Array<PersonsModel>;
  
  /** 
   * Current session model containing authentication and user session data
   * @type {SessionModel}
   */
  public sessionModel: SessionModel;
  
  /** 
   * User model containing user details and credentials
   * @type {UserModel}
   */
  public userModel: UserModel;
  
  /** 
   * Array of all persons in the system
   * @type {Array<PersonsModel>}
   */
  public persons: Array<PersonsModel>;
  
  /** 
   * Currently logged-in person/staff member details
   * @type {PersonsModel}
   */
  public person: PersonsModel;
  
  /** 
   * Reactive form group for login form validation
   * Controls: userName (required), password (required), rememberme (optional)
   * @type {FormGroup}
   */
  public loginForm: FormGroup;
  
  /** 
   * Flag indicating if login form has been submitted
   * Used to trigger validation error display
   * @type {boolean}
   * @default false
   */
  public submitted = false;
  
  /** 
   * Parsed user data from localStorage
   * @type {any}
   */
  public userData: any;
  
  /** 
   * Login username/identifier
   * @type {string}
   */
  public LoginUserName: string;
  
  /** 
   * Person type of logged-in user (Farmer, Provider, Admin, SME, etc.)
   * @type {string}
   */
  public loginPersonType: string;
  
  /** 
   * Business category of the logged-in user's business (Farm, Provider, Others)
   * @type {string}
   */
  public loginBusinessCategory: string;
  
  /** 
   * Mobile number of the logged-in person
   * @type {string}
   */
  public loginPersonMobileNumber: string;
  
  /** 
   * Full name of the logged-in person (First name + Last name)
   * @type {string}
   */
  public loginPersonName: string;
  
  /** 
   * Unique identifier of the logged-in person
   * @type {string}
   */
  public loginPersonId: string;
  
  /** 
   * User ID from URL parameters for direct user login
   * @type {string}
   */
  public paramsloginuserId: string;
  
  /** 
   * Flag to control login page visibility
   * @type {boolean}
   * @default false
   */
  public showLoginPage: boolean = false;
  
  /** 
   * Type of business (Farm, Provider, SME, etc.)
   * @type {string}
   */
  public loginBusinessType: string;
  
  /** 
   * Icon/logo associated with the business
   * @type {any}
   */
  public loginBusinessIcon: any;
  
  /** 
   * Flag indicating if guest/external login was clicked
   * @type {boolean}
   */
  public guestClick: boolean;
  
  /** 
   * Flag indicating if login is from Ionic mobile application
   * @type {boolean}
   */
  fromIonic: boolean;

  /**
   * Constructor for LoginComponent
   * 
   * Initializes the login component with all required services and sets up the login form
   * with validation rules.
   * 
   * Services injected:
   * - SessionService: Manages user authentication and session lifecycle
   * - Router: Angular router for navigation after login
   * - ActivatedRoute: Access to route parameters and query strings
   * - PersonsService: Retrieve and manage person/staff information
   * - BusinessEntityService: Business entity data management
   * - FarmSiteService: Farm site data management
   * - UserService: User account operations and password management
   * - Sidenavaction: Side navigation menu management
   * - FormBuilder: Angular reactive form builder
   * - FirebaseNotificationsMessagingService: Push notification and Firebase messaging
   * - LocationStrategy: Current URL/location information
   * - PreviousRouteService: Navigation history tracking
   * - PolicyCheckerService: Access control policies and subscriptions
   * - AccessControllerService: Role-based access control
   * 
   * Form validation:
   * - userName: Required field
   * - password: Required field
   * - rememberme: Optional field
   * 
   * @constructor
   * @param {SessionService} sessionService - Service for session management and authentication
   * @service SessionService
   * @param {Router} router - Angular router for navigation
   * @service Router
   * @param {ActivatedRoute} route - Active route and query parameters
   * @service ActivatedRoute
   * @param {PersonsService} personService - Service for person/staff data
   * @service PersonsService
   * @param {BusinessEntityService} businessEntityService - Service for business entity data
   * @service BusinessEntityService
   * @param {FarmSiteService} farmsiteEntityService - Service for farm site data
   * @service FarmSiteService
   * @param {UserService} userService - Service for user operations
   * @service UserService
   * @param {Sidenavaction} sidenavaction - Service for side navigation management
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Angular form builder for reactive forms
   * @service FormBuilder
   * @param {FirebaseNotificationsMessagingService} firebaseMessagingService - Firebase messaging service
   * @service FirebaseNotificationsMessagingService
   * @param {LocationStrategy} locationStrategy - Browser location strategy
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for navigation history
   * @service PreviousRouteService
   * @param {PolicyCheckerService} policyCheckerService - Service for access control policies
   * @service PolicyCheckerService
   * @param {AccessControllerService} accessControllerService - Service for role-based access control
   * @service AccessControllerService
   */
  constructor(public sessionService: SessionService, public router: Router, public route: ActivatedRoute, public personService: PersonsService, public businessEntityService: BusinessEntityService, public farmsiteEntityService: FarmSiteService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder,
              private firebaseMessagingService: FirebaseNotificationsMessagingService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService,
            private policyCheckerService: PolicyCheckerService, private accessControllerService: AccessControllerService) {
    this.sessionModel = new SessionModel();
    /*validation */
    this.loginForm = this.formBuilder.group({
      userName: ['', Validators.required],
      password: ['', [Validators.required]],
      rememberme: [null]
    });
    /*validation ends */
  }

  /**
   * Angular component initialization lifecycle hook
   * 
   * Performs comprehensive login initialization including:
   * 1. Session validation - Checks if session already exists and logs out if needed
   * 2. Navigation history tracking - Stores current route in previous route service
   * 3. Query parameter handling - Processes token and redirect parameters from URL
   * 4. Token validation flow - Validates authentication tokens from external apps (Ionic)
   * 5. URL parameter handling - Processes user ID from URL parameters
   * 6. Form state management - Initializes form fields and state
   * 7. Auto-login for Ionic - Handles automatic login from Ionic mobile app
   * 
   * Authentication flows handled:
   * - Direct URL user ID login (getuserDetailsByuserid)
   * - Token-based login (validateToken with redirect)
   * - Ionic mobile app login (auto-fill and submit)
   * 
   * Token validation:
   * - Validates token from external applications
   * - Stores token in localStorage for authenticated API calls
   * - Handles token expiry with redirect to login
   * 
   * Ionic flow:
   * - Detects Ionic redirect parameter
   * - Auto-fills mobile number and password
   * - Disables input fields
   * - Auto-submits login form
   * - Stores Ionic-specific session flags
   * - Initiates person type resolution
   * 
   * Subscriptions:
   * - queryParams: For token and redirect URL parameters
   * - params: For user ID in route parameters
   * 
   * localStorage mutations:
   * - Sets: redirectAfterLogin, authToken, isLoggedIn, isLoggedInMobile
   * - Clears: On navigation away or session end
   * 
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook - Called once after component creation
   */
  public async ngOnInit() {
    let sessionExist = localStorage.getItem('isSessionExist');
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    if (sessionExist == 'yes') {
      this.sessionModel = await this.sessionService.sessionLogout(localStorage.userId);
      localStorage.clear();
      this.sessionModel = new SessionModel();
      this.sessionModel.userName = undefined;
      this.sessionModel.password = undefined;
      this.router.navigate(['/login']);
      window.location.reload();
    }
  }

  /**
   * Getter method to access login form controls
   * 
   * Provides convenient access to form controls in templates using the syntax f.controlName.
   * Used for validation error display and field state checking.
   * 
   * Available controls:
   * - f.userName: Username/mobile number field
   * - f.password: Password field
   * - f.rememberme: Remember me checkbox
   * 
   * @method f
   * @returns {any} - The form controls object with all form field references
   * 
   * @example
   * // In template:
   * <div *ngIf="f.userName?.invalid && (f.userName?.dirty || f.userName?.touched)">
   *   Username is required
   * </div>
   */
  get f() {
    return this.loginForm.controls;
  }

  /**
   * Handles user login form submission
   * 
   * Performs user authentication and post-login session initialization. This is the main
   * login entry point for direct username/password login flow.
   * 
   * Login flow:
   * 1. Marks form as submitted for validation error display
   * 2. Validates login form structure (required fields, formats)
   * 3. Retrieves password from form
   * 4. Calls SessionService to authenticate user
   * 5. On successful authentication:
   *    - Stores session data in localStorage (user info, IDs, types)
   *    - Clears form submitted flag
   *    - Initializes person type and business information
   *    - Requests Firebase notification permissions
   * 6. On authentication failure:
   *    - Navigates to home page
   * 
   * localStorage keys set on successful login:
   * - isLoggedIn: 'true'
   * - isLoggedInMobile: 'true'
   * - aquLoginData: User JSON
   * - isSessionExist: 'yes'
   * - businessID, personID, userID, userName, userType
   * 
   * Post-login processing:
   * - Calls getPersonTypeByPersonId() for role-based routing
   * - Calls firebaseMessagingService.requestNotificationPermission()
   * 
   * @async
   * @method onLogin
   * @returns {Promise<void>}
   * @throws {Error} If session login fails, error is displayed via alert
   * 
   * @example
   * // Called from login form submission
   * <form (ngSubmit)="onLogin()">
   *   <input formControlName="userName" type="text" />
   *   <input formControlName="password" type="password" />
   *   <button type="submit">Login</button>
   * </form>
   */
  public async onLogin() {
    try {
      this.submitted = true;
      // stop here if form is invalid
      if (this.loginForm.invalid) {
        return;
      }
      this.submitted = false;
      let password = this.sessionModel.password;
      this.sessionModel = await this.sessionService.sessionLogin(this.sessionModel.userName, this.sessionModel.password, 'blubay');
      if (this.sessionModel) {
        this.sessionModel.password = password;
        // Mark the user as logged in
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('isLoggedInMobile', 'true');
        localStorage.setItem('aquLoginData', JSON.stringify(this.sessionModel.user));
        this.userData = JSON.parse(localStorage.aquLoginData);
        localStorage.setItem('isSessionExist', 'yes');
        localStorage.setItem('businessID', this.userData.businessID); //storing login businessID to localstorage.
        localStorage.setItem('personID', this.userData.personID);     //storing login personID to localstorage.
        localStorage.setItem('userID', this.userData.id);             //storing login userID to localstorage.
        localStorage.setItem('userName', this.userData.userName);     //storing login userName to localstorage.
        localStorage.setItem('userType', this.userData.user_type);    //storing login userType to localstorage.
        // PersonDetails By personID
        await this.getPersonTypeByPersonId();
      } else {
        this.router.navigate(['/']);
      }
    } catch (error) {
      alert(error);
    }
  }

  /**
   * Retrieves and initializes person type and business information after login
   * 
   * Comprehensive post-login processing that:
   * 1. Retrieves all persons associated with the logged-in business
   * 2. Finds the current logged-in person from the list
   * 3. Extracts person details (type, name, mobile, ID)
   * 4. Stores person information in localStorage
   * 5. Retrieves business entity details by person reference
   * 6. Validates business account status and person type permissions
   * 7. Initializes access control policies and feature subscriptions
   * 8. Implements role-based routing to appropriate dashboard
   * 9. Requests Firebase notification permissions
   * 
   * Person types handled:
   * - Business Admin: Full access to all business features
   * - Farmer: Farm operator with limited access
   * - Farm Manager: Farm staff management
   * - Provider: Service provider user
   * - Provider Manager: Provider staff management
   * - SME: Small/medium enterprise user
   * - Buyer: Purchasing user
   * - Employee: Business employee
   * - Guest: Guest access
   * 
   * Business categories:
   * - Farm: Agricultural business
   * - Provider: Service provider business
   * - Others: Other business types
   * 
   * Validation checks:
   * - Person exists and has person type
   * - Address/dialing code available
   * - Business account not inactivated (unless Farm/Provider Manager)
   * - Person type matches approved roles
   * - Business status is valid
   * - Access policies initialized
   * - Feature subscriptions available
   * 
   * Routing destinations based on role:
   * - Admin users: /home
   * - Farmers/Farm Managers (with BA): /home
   * - Farmers/Farm Managers (without BA): /home/Persons-List/Staff
   * - Provider Business Admin: /home/dashboard
   * - Provider Managers: /home/visits or /home/dashboard
   * - SME/Buyer: /smelandingpage
   * - Guest: /ambro_marketconnect
   * - Account closure: /home/account-closure
   * 
   * localStorage mutations:
   * - Stores: loginPersonType, loginPersonId, loginPersonName, loginPersonMobileNumber
   * - Stores: loginBusinessType, loginBusinessName, loginBusinessID, loginBusinessCategory
   * - Stores: businessEntityById (full entity JSON)
   * - Retrieves: redirectAfterLogin for post-login navigation
   * - Removes: redirectAfterLogin after processing
   * 
   * Error handling:
   * - Missing address/dialing code: Shows alert, prevents login
   * - Invalid person type: Shows alert, prevents login
   * - Inactivated account: Shows alert for managers, navigates to closure for others
   * - Farm with provider access: Shows alert, clears session
   * - No role permissions: Shows alert, clears session
   * - Catches all exceptions with alert display
   * 
   * @async
   * @method getPersonTypeByPersonId
   * @returns {Promise<void>}
   * @throws {Error} If data retrieval fails
   * 
   * @example
   * await this.getPersonTypeByPersonId(); // Called after successful login
   */
  public async getPersonTypeByPersonId() {
    try {
      if (localStorage.personID) {
        this.loginBusinessPersons = await this.personService.getAllPersonsByBusinessId(localStorage.businessID);
        this.person = this.loginBusinessPersons && this.loginBusinessPersons.length > 0 ? this.loginBusinessPersons.find((personObj: PersonsModel) => personObj.id === localStorage.personID) : undefined;
        if (this.person && this.person.personType && this.person.personType.length > 0) {
          this.businessEntity = this.person.businessID;
          this.loginPersonType = this.person.personType[0].name;
          this.loginPersonName = this.person.firstName + ' ' + this.person.lastName;
          this.loginPersonId = this.person.id;
          this.loginPersonMobileNumber = this.person.mobileNumber;
          //storing login personType to localstorage.
          localStorage.setItem('loginPersonType', this.loginPersonType);
          localStorage.setItem('loginPersonId', this.loginPersonId);
          localStorage.setItem('loginPersonName', this.loginPersonName);
          localStorage.setItem('loginPersonMobileNumber', this.loginPersonMobileNumber);
          //storing login person name to localstorage.
          localStorage.setItem('LoginUserName', this.person.firstName + ' ' + this.person.lastName);
          this.LoginUserName = localStorage.getItem('LoginUserName');
          let BAPersonAvailable: any;
          // After login, check for the redirect parameter
          const redirectTo = localStorage.getItem('redirectAfterLogin');
          if (localStorage.appName != 'GUEST') {
            //storing businessEntity to localstorage.
            BAPersonAvailable = this.loginBusinessPersons && this.loginBusinessPersons.length > 0 ? this.loginBusinessPersons.filter(bPersons => ['618a5b620f339c001e991b31'].includes(bPersons && bPersons.personType[0].personRoleID)) : [];
            this.loginBusinessType = this.businessEntity.businessType[0].name;
            localStorage.setItem('businessEntityById', JSON.stringify(this.businessEntity));
            //storing login business type to localstorage.
            localStorage.setItem('loginBusinessType', this.loginBusinessType);
            if (!['SME', 'Buyer'].includes(this.loginPersonType)) {
              this.loginBusinessCategory = this.businessEntity.businessCategory[0].name;
              localStorage.setItem('loginBusinessCategory', this.businessEntity.businessCategory[0].name);
            }
            localStorage.getItem(this.loginBusinessType);
            //storing login businessName to localstorage.
            localStorage.setItem('loginBusinessName', this.businessEntity.businessName);
            localStorage.setItem('loginBusinessID', this.businessEntity.id);
          }

          // Initialize subscriptions (role + plan based features)
          const accessibleFeatures = await this.policyCheckerService.initializeSubscriptions();

          // Build navigation structure if features exist
          let isNavInitialized = false;
          if (accessibleFeatures && accessibleFeatures.length > 0) {
            isNavInitialized = this.accessControllerService.isBuildArchitectResolved();
          }
          if((['Farmer', 'Business Admin'].includes(this.loginPersonType) && this.loginBusinessCategory == 'Farm' && this.businessEntity.status == '') || this.loginPersonType == 'EMPLOYEE') {
            if (isNavInitialized) {
              if (this.businessEntity && this.businessEntity.accountStatus == 'inactivated') {
                this.router.navigate(['/home/account-closure']);
              } else {
                if (((this.loginPersonType == 'Farmer' && BAPersonAvailable.length > 0) || this.loginPersonType == 'Business Admin') && this.businessEntity.status == '' && this.loginBusinessCategory == 'Farm') {
                  this.router.navigate(['/home']);
                } else if ((this.loginPersonType == 'Farmer' && BAPersonAvailable.length == 0) && this.businessEntity.status == '') {
                  this.router.navigate(['/home/Persons-List/Staff']);
                } else {
                  this.router.navigate(['/home']);
                }
                if (localStorage.appName != 'GUEST') {
                  if ((this.loginBusinessType == 'Farm' && this.businessEntity.providerID.length > 0)) {
                    alert('Oops! You do not have access to dashboard. Contact your Business Owner');
                    localStorage.clear();
                    this.router.navigate(['/login']);
                    this.sessionModel.userName = '';
                    this.sessionModel.password = '';
                  }
                }
              }
            }
          } else {
            console.log('isNavInitialized', isNavInitialized, this.loginPersonType);
            alert('Oops! You do not have access to dashboard');
            localStorage.clear();
            this.router.navigate(['/login']);
            this.sessionModel.userName = '';
            this.sessionModel.password = '';
          }
        }
      }
      this.firebaseMessagingService.requestNotificationPermission();
      // Clear the temporary redirect key after processing
      localStorage.removeItem('redirectAfterLogin');
    } catch (error) {
      alert(error);
    }
  }
}
